import {type Edge, type Node, type XYPosition} from 'reactflow'
import {TemplateMessage} from '../../MyTemplate/CreateScratch/Template'

export interface INodePosition {
  [key: string]: XYPosition
}

export interface IChannel {
  type: string
  icon: string
  color: string
  svg: string
  isVisible: boolean
  bgColor?: string
  altText?: string
}

export interface IManualTab {
  key: string
  labelKey: string
}

export interface IAutomationAudience {
  key: string
  value: string
  icon: string
  titleKey: string
  descKey: string
  svg: string
}

export interface ISection {
  title: string
  items: readonly IChannel[]
  moreInfo?: string
}

export interface ITriggerOption {
  value: string
  labelKey: string
}

export interface LayoutedElements {
  nodes: Node[]
  edges: Edge[]
}

export interface AddNodeMessage {
  type: 'ADD_NODE_BETWEEN_EDGES'
  payload: {
    node: Node
    edges: Edge[]
    edgeToRemove: string
  }
}

export interface MetaDataWithDelay {
  delay: IDelayUnit
}

export interface MetaDataWithCondition {
  condition: ICondition[]
}

export interface MetaDataWithSMS {
  sms?: TemplateMessage
  sender?: string
  senderType?: string
}

export interface TagOption {
  value: string
  label: string
}

export interface MetaDataWithAddTag {
  tags: TagOption[]
}

export interface DataNode<T> {
  label?: string
  metaData?: T
}

export type MetaData =
  | MetaDataWithDelay
  | MetaDataWithCondition
  | MetaDataWithSMS
  | MetaDataWithAddTag

export enum DelayTime {
  ABSOLUTE = 'absolute',
  RELATIVE = 'relative',
}

export enum DelayUnit {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
}

export type DelayValue = number | string
export interface IDelayUnit {
  type: DelayTime
  value: DelayValue
  unit: DelayUnit
  timeZone?: string
  timeZoneTime?: string
  timeFromBrowser?: string
}

export enum ConditionType {
  SENT = 'sent',
  FAILED = 'failed',
  DELIVERED = 'delivered',
  REPLY_CONTAIN = 'reply_contain',
  REPLY_START_WITH = 'reply_start_with',
  REPLY_END_WITH = 'reply_end_with',
  EQUAL = 'equal',
  ANY_MESSAGE_EXCEPT_OPTOUT = 'any_message_except_optout',
}

export interface ICondition {
  type: ConditionType
  value: string | string[]
}
export enum FlowStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface FlowBusinessHoursSetting {
  status: boolean
  startTime: string
  endTime: string
  days: number[]
  timezone: string
}

export interface Flow {
  _id: string
  name: string
  description?: string
  nodes: Node<DataNode<any>>[]
  edges: Edge[]
  status: FlowStatus
  createdAt: string
  updatedAt: string
  isTemplate: boolean
  scope: string
  affiliate: string
  industry: string
  settings?: {
    isStopOnReply?: boolean
    allowMultipleBitesNotifications?: boolean
    businessHours?: FlowBusinessHoursSetting
    [key: string]: unknown
  }
  statistics: {
    totalInstances: number
    totalCustomers: number
    activeCustomers: number
    completedCustomers: number
    failedCustomers: number
    statusBreakdown: {
      pending: number
      running: number
      waiting: number
      completed: number
      failed: number
      cancelled: number
      paused: number
      bites?: number
    }
  }

  // pause
  isSentEmailPause: boolean
  isSentNotificationPause: boolean
  haveJobPaused: boolean
}

export interface FlowPaused {
  total: number
}

export interface FlowStats {
  total: number
  active: number
  inactive: number
}

export interface FlowPayload {
  name: string
  scope?: string
  industry?: string
  affiliate?: string
  nodes: Node<DataNode<any>>[]
  edges: Edge[]
  status: FlowStatus
  settings?: {
    isStopOnReply?: boolean
    allowMultipleBitesNotifications?: boolean
    businessHours?: FlowBusinessHoursSetting
    customerWaitingReplyWindowMinutes?: number
    [key: string]: unknown
  }
}

export enum FlowReportRateType {
  BOUNCE_RATE = 'bounce_rate',
  CTR_RATE = 'ctr_rate',
  REPLY_RATE = 'reply_rate',
  UNSUBSCRIBE_RATE = 'unsubscribe_rate',
}

export enum FlowReportStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface FlowReportRate {
  value: number
  type: FlowReportRateType
  growthRate: number
}

export interface FlowReport {
  smsFlowId: string
  bounceRate: FlowReportRate
  ctrRate: FlowReportRate
  replyRate: FlowReportRate
  unsubscribeRate: FlowReportRate
  dayActive: number
  totalInProgress: number
  totalCompleted: number
  totalStarted: number
  status: FlowReportStatus
  flowName?: string
}

export interface FlowTemplateStats {
  templateId: string
  nodeId: string
  templateName?: string
  sent: number
  received: number
  unsubscribed: number
  clickRate: {
    value: number
    type: string
    growthRate: number
  }
  bounced: number
}

export interface IFlowStatusBreakdown {
  pending: number
  running: number
  waiting: number
  completed: number
  failed: number
  cancelled: number
  paused: number
  bites: number
  started: number
}

export interface IFlowStatisticsSummary {
  totalInstances: number
  totalCustomers: number
  totalBites: number
  activeCustomers: number
  completedCustomers: number
  failedCustomers: number
  statusBreakdown: IFlowStatusBreakdown
}
